import React, { useEffect, useState } from 'react';
import { sendEmailVerification, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../lib/firebase';
import { EnvelopeIcon, ArrowPathIcon, XMarkIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

/**
 * Tartós, kezelhető sáv: e-mail/jelszavas fióknál, ha az e-mail nincs megerősítve.
 * Innen újraküldhető a megerősítő levél, és „Megerősítettem” gombbal frissíthető az állapot –
 * onboarding alatt is, amikor a Profil még nem elérhető.
 */
const EmailVerificationBanner: React.FC<{ compact?: boolean }> = ({ compact }) => {
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [dismissed, setDismissed] = useState(() => { try { return sessionStorage.getItem('emailVerifyBannerDismissed') === '1'; } catch { return false; } });

  const evaluate = () => {
    const u = auth.currentUser;
    const isEmailUser = !!u && u.providerData.some(p => p.providerId === 'password');
    setVisible(!!u && isEmailUser && !u.emailVerified);
  };
  useEffect(() => { evaluate(); const unsub = onAuthStateChanged(auth, evaluate); return () => unsub(); }, []);
  useEffect(() => { if (cooldown <= 0) return; const t = setTimeout(() => setCooldown(c => c - 1), 1000); return () => clearTimeout(t); }, [cooldown]);

  if (!visible || dismissed) return null;

  const resend = async () => {
    const u = auth.currentUser; if (!u) return;
    try {
      setBusy(true);
      await sendEmailVerification(u, { url: `${window.location.origin}/login` });
      toast.success(`Megerősítő e-mail elküldve: ${u.email}`);
      setCooldown(60);
    } catch (e: any) {
      toast.error(e?.code === 'auth/too-many-requests' ? 'Túl sok kérés – próbáld pár perc múlva.' : 'Nem sikerült elküldeni a megerősítő e-mailt');
    } finally { setBusy(false); }
  };
  const recheck = async () => {
    const u = auth.currentUser; if (!u) return;
    try { setBusy(true); await u.reload(); evaluate(); if (auth.currentUser?.emailVerified) toast.success('E-mail cím megerősítve. Köszönjük!'); else toast('Még nincs megerősítve – nézd meg a levelet (spam mappát is).'); }
    finally { setBusy(false); }
  };

  return (
    <div className={`flex flex-col sm:flex-row sm:items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20 text-amber-900 dark:text-amber-200 ${compact ? 'px-3 py-2 text-xs' : 'px-4 py-3 text-sm'}`}>
      <EnvelopeIcon className="w-5 h-5 flex-shrink-0 hidden sm:block" />
      <div className="flex-1 min-w-0">
        <span className="font-medium">Az e-mail címed még nincs megerősítve.</span>{' '}
        <span className="opacity-80">Küldtünk egy linket a(z) <strong>{auth.currentUser?.email}</strong> címre – kattints rá, aztán jelezd itt.</span>
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <button type="button" onClick={resend} disabled={busy || cooldown > 0} className="h-8 px-3 rounded-lg bg-amber-600 text-white text-xs font-medium hover:bg-amber-700 disabled:opacity-50 inline-flex items-center gap-1">
          <EnvelopeIcon className="w-3.5 h-3.5" />{cooldown > 0 ? `Újraküldés (${cooldown}s)` : 'Újraküldés'}
        </button>
        <button type="button" onClick={recheck} disabled={busy} className="h-8 px-3 rounded-lg border border-amber-300 dark:border-amber-700 text-xs font-medium hover:bg-amber-100 dark:hover:bg-amber-900/40 inline-flex items-center gap-1">
          <ArrowPathIcon className="w-3.5 h-3.5" />Megerősítettem
        </button>
        <button type="button" onClick={() => { setDismissed(true); try { sessionStorage.setItem('emailVerifyBannerDismissed', '1'); } catch { /* ignore */ } }} className="p-1.5 rounded-md hover:bg-amber-100 dark:hover:bg-amber-900/40" aria-label="Bezárás"><XMarkIcon className="w-4 h-4" /></button>
      </div>
    </div>
  );
};

export default EmailVerificationBanner;
